import { FC, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card } from '@altermaind/web/ui';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { Employer } from '@/people/state/types/employer';
import { useDialog } from '../../hooks';
import { AlertBadge } from '../AlertBadge';
import { BackgroundBox } from './BackgroundBox';
import { ContentProfile } from './ContentProfile';
import { DeleteProfile } from './DeleteProfile';
import { DisableProfile } from './DisableProfile';
import { EditProfile } from './EditProfile';
import { EnableProfile } from './EnableProfile';
import { HeaderProfile } from './HeaderProfile';
import { Settings } from './Settings';

type Props = {
  employer: Employer;
  hasSettings?: boolean;
  showErrorsProfile?: boolean;
  hasAlertData?: boolean;
  onEditChange?: (isEditing: boolean) => void;
};

/**
 * Profile card component for displaying employer information.
 * @param employer - The employer data to display.
 * @param hasSettings - Flag to show settings options (edit, disable, delete).
 * @param showErrorsProfile - Flag to display profile validation errors.
 * @param hasAlertData - Flag to show alert badge data.
 * @param onEditChange - Callback fired when edit mode changes.
 * @returns JSX.Element
 */
export const ProfileCard: FC<Props> = ({
  employer,
  hasSettings = false,
  showErrorsProfile = false,
  hasAlertData = false,
  onEditChange,
}) => {
  const t = useTranslations('profileCard.alerts');
  const { genericResponsiveValue } = useResponsiveStyle();

  const [edit, setEdit] = useState(false);

  const [showCtaProfile, setShowCtaProfile] = useState<'none' | 'block'>('none');

  const { isOpen: isOpenDisableProfile, closeDialog: closeDialogDisableProfile } =
    useDialog('disableProfile');

  const { isOpen: isOpenDeleteProfile, closeDialog: closeDialogDeleteProfile } =
    useDialog('deleteProfile');

  const { isOpen: isOpenEnableProfile, closeDialog: closeDialogEnableProfile } =
    useDialog('enableProfile');

  const onEdit = (value: boolean) => {
    setEdit(value);
    setShowCtaProfile('none');
    onEditChange?.(value);
  };

  if (edit) {
    return <EditProfile employer={employer} setEdit={onEdit} />;
  }

  return (
    <>
      <Card p='none'>
        <BackgroundBox />
        <Box
          pt={genericResponsiveValue({
            breakPoints: { desktop: 'none' },
            defaultValue: 'xs',
          })}
          px={genericResponsiveValue({
            breakPoints: { desktop: 'm' },
            defaultValue: 's',
          })}
          position='relative'
        >
          <Box display='flex' flexDirection='row' justifyContent='space-between'>
            <HeaderProfile fullName={`${employer.firstName || ''} ${employer.lastName || ''}`} />
            <Box display='flex'>
              {hasSettings && (
                <Settings
                  setShowCtaProfile={setShowCtaProfile}
                  showCtaProfile={showCtaProfile}
                  onEdit={onEdit}
                  employer={employer}
                />
              )}
            </Box>
          </Box>
        </Box>
        <ContentProfile showErrorsProfile={showErrorsProfile} employer={employer} />
        {showErrorsProfile && (
          <>
            {employer.issues.length === 1 &&
              employer.issues.find(issue => issue.fieldName === 'manager') && (
                <Box p='m' pt='none' mt='s'>
                  <AlertBadge type='error' iconName='info' label={t('managerWrong')} />
                </Box>
              )}
            {employer.issues.length > 0 &&
              employer.issues.find(issue => issue.fieldName !== 'manager') && (
                <Box p='m' pt='none' mt='s'>
                  <AlertBadge
                    type='error'
                    iconName='info'
                    label={t(employer.issues.length === 1 ? 'hasIssue' : 'hasIssues', {
                      count: employer.issues.length,
                    })}
                  />
                </Box>
              )}
          </>
        )}
        {hasAlertData && (
          <Box p='m' pt='none' mt='s'>
            <AlertBadge type='transparent' iconName='lightBulb' label={t('alertData')} />
          </Box>
        )}
      </Card>
      {hasSettings && (
        <>
          <EnableProfile
            isOpen={isOpenEnableProfile}
            closeDialog={closeDialogEnableProfile}
            employeeId={employer.id}
          />
          <DisableProfile
            isOpen={isOpenDisableProfile}
            closeDialog={closeDialogDisableProfile}
            employeeId={employer.id}
          />
          <DeleteProfile
            isOpen={isOpenDeleteProfile}
            closeDialog={closeDialogDeleteProfile}
            employeeId={employer.id}
          />
        </>
      )}
    </>
  );
};
